import type { OrderDetailResponse } from "@/types/orderDetails";
import type {
  ClientData,
  Address,
  OrderProduct,
  OrderSummaryData,
} from "@/types/orderDetails";
import { mapCarrierIdToType } from "./carrierMapper";
import { formatPhoneWithIndicatif } from "./phoneIndicatif";
import { isEtranger, calculateTVARate } from "./tvaUtils";

/**
 * Mappe les données de l'API vers ClientData
 */
export function mapToClientData(data: OrderDetailResponse): ClientData {
  const safeData = data as Partial<OrderDetailResponse> & {
    acheteur?: Partial<OrderDetailResponse["acheteur"]> | null;
    livraison?: Partial<OrderDetailResponse["livraison"]> | null;
    paiement?: Partial<NonNullable<OrderDetailResponse["paiement"]>> | null;
  };

  // Utiliser dateCreation si dateCommande est null ou "0000-00-00 00:00:00"
  let orderDate: string | undefined = undefined;
  if (
    safeData.dateCommande &&
    safeData.dateCommande !== "0000-00-00 00:00:00"
  ) {
    orderDate = safeData.dateCommande;
  } else if (safeData.dateCreation) {
    orderDate = safeData.dateCreation;
  } else if (safeData.commandeGroupe?.date_creation) {
    orderDate = safeData.commandeGroupe.date_creation;
  }

  const acheteur = safeData.acheteur;
  const livraison = safeData.livraison;
  const eori = acheteur?.eori ? String(acheteur.eori).trim() || undefined : undefined;

  const fullName =
    `${acheteur?.nomContact || ""} ${acheteur?.prenomContact || ""}`.trim() ||
    undefined;
  const idLivraison = livraison?.id_livraison;
  const hasValidCarrierId =
    typeof idLivraison === "number" && !Number.isNaN(idLivraison);
  const carrier = hasValidCarrierId
    ? mapCarrierIdToType(idLivraison)
    : undefined;
  const carrierService = livraison?.libelle
    ? `${livraison.libelle} par Efashion Paris`
    : undefined;

  const orderNumberSource =
    safeData.id_commande_groupe ?? safeData.commandeGroupe?.id_commande_groupe;

  return {
    orderNumber:
      orderNumberSource != null ? String(orderNumberSource) : undefined,
    orderDate,
    fullName,
    email: acheteur?.email || undefined,
    siret: acheteur?.tva_intra || undefined,
    eori,
    paymentMethod: safeData.paiement?.texte_fr || "",
    carrier,
    carrierService,
  };
}

/**
 * Mappe les données de l'API vers Address (livraison)
 */
export function mapToDeliveryAddress(data: OrderDetailResponse): Address {
  if (!data.adresseLivraison) {
    return {
      enseigne: "",
      contact: "",
      adresse: "",
      codePostal: "",
      ville: "",
      pays: "",
      telephone: "",
    };
  }

  // Si Societe est vide, utiliser nomSociete de l'acheteur (comme dans l'ancien système)
  const enseigne = data.adresseLivraison.Societe
    ? data.adresseLivraison.Societe
    : data.acheteur?.nomSociete || "";

  return {
    enseigne,
    contact: data.adresseLivraison.NomContact || "",
    adresse: data.adresseLivraison.adresse || "",
    codePostal: data.adresseLivraison.codePostal || "",
    ville: data.adresseLivraison.ville || "",
    pays: data.adresseLivraison.pays?.texte_fr || "",
    telephone: formatPhoneWithIndicatif(
      data.adresseLivraison.telephone || data.adresseLivraison.mobile || "",
      data.adresseLivraison.pays?.code,
    ),
  };
}

/**
 * Mappe les données de l'API vers Address (facturation)
 */
export function mapToBillingAddress(data: OrderDetailResponse): Address {
  if (!data.adresseFacturation) {
    return {
      enseigne: "",
      contact: "",
      adresse: "",
      codePostal: "",
      ville: "",
      pays: "",
      telephone: "",
    };
  }

  // Si Societe est vide, utiliser nomSociete de l'acheteur (comme dans l'ancien système)
  const enseigne = data.adresseFacturation.Societe
    ? data.adresseFacturation.Societe
    : data.acheteur?.nomSociete || "";

  return {
    enseigne,
    contact: data.adresseFacturation.NomContact || "",
    adresse: data.adresseFacturation.adresse || "",
    codePostal: data.adresseFacturation.codePostal || "",
    ville: data.adresseFacturation.ville || "",
    pays: data.adresseFacturation.pays?.texte_fr || "",
    telephone: formatPhoneWithIndicatif(
      data.adresseFacturation.telephone || data.adresseFacturation.mobile || "",
      data.adresseFacturation.pays?.code,
    ),
  };
}

/**
 * Mappe les données de l'API vers OrderProduct[] (produits actifs, corbeille = 0)
 */
export function mapToProducts(data: OrderDetailResponse): OrderProduct[] {
  if (!data.lignes || !Array.isArray(data.lignes)) {
    return [];
  }

  // Vérifier si le client est étranger (ne paye pas la TVA)
  const clientEtranger = isEtranger(data);

  // Calculer le taux de TVA selon la date de la commande
  const tvaRate = calculateTVARate(data.dateCommande || data.dateCreation);

  return data.lignes
    .filter((ligne) => ligne.corbeille === 0) // Exclure les produits en rupture
    .map((ligne) => {
      const quantites = ligne.quantites;
      const quantiteTotal =
        ligne.quantite_total ||
        quantites.q1 +
          quantites.q2 +
          quantites.q3 +
          quantites.q4 +
          quantites.q5 +
          quantites.q6 +
          quantites.q7 +
          quantites.q8 +
          quantites.q9 +
          quantites.q10 +
          quantites.q11 +
          quantites.q12;

      // Prix unitaire normal (prix du produit)
      const prixUnitaireNormal = ligne.prix || 0;

      // Prix ligne HT (prixLigne de la ligne - contient déjà la remise produit si applicable)
      // Note: La remise de commande n'est PAS appliquée ligne par ligne, seulement sur le total
      const prixLigneHT = ligne.prixLigne || 0;

      // Prix unitaire calculé comme dans l'ancien système : prixLigne / quantiteTotal
      // (comme dans l'ancien système : $ligne['prixLigne'] / $ligne['quantite_modeles'])
      const prixUnitaire =
        quantiteTotal > 0
          ? prixLigneHT / quantiteTotal
          : ligne.prixReduit || ligne.prix || 0;

      // Prix ligne HT avant remise produit (si prixReduit existe)
      // Calcul: prix unitaire normal * quantité totale
      const prixLigneHTAvantRemiseProduit = prixUnitaireNormal * quantiteTotal;

      // Vérifier si cette ligne a une remise produit (prixReduit différent de prix)
      const hasRemiseProduit =
        ligne.prixReduit != null &&
        ligne.prixReduit > 0 &&
        ligne.prix != null &&
        ligne.prix > 0 &&
        ligne.prixReduit < ligne.prix;

      // Prix unitaire normal (avant remise) pour affichage barré si remise
      // Comme dans l'ancien système : ligne_prix_src_produit
      const prixUnitaireNormalAffichage =
        quantiteTotal > 0 ? prixUnitaireNormal : undefined;

      // Prix ligne TTC (seulement si pas étranger)
      const prixLigneTTC = clientEtranger
        ? prixLigneHT
        : prixLigneHT * (1 + tvaRate);

      // Poids total de la ligne (poids unitaire * quantité)
      const poidsTotal =
        ligne.poids || (ligne.poids_produit || 0) * quantiteTotal;

      // Couleur (utiliser FR par défaut, sinon EN/UK)
      const couleur =
        ligne.couleur_FR || ligne.couleur_EN || ligne.couleur_UK || "";

      // Construire l'URL de l'image (le backend gère déjà la recherche dans le nouveau puis l'ancien système)
      const imageUrl: string | null = ligne.imageUrl || null;

      // Utiliser reference_base directement depuis la base de données
      const referenceBase = ligne.reference_base || ligne.reference || "";

      return {
        id: ligne.id_ligne,
        productId: ligne.id_produit,
        reference: ligne.reference || "",
        referenceBase,
        price: prixLigneHT, // Prix ligne HT (comme dans l'ancien système)
        priceUnit: prixUnitaire, // Prix unitaire (après remise produit si applicable) - ligne_prix_produit
        priceUnitNormal: hasRemiseProduit
          ? prixUnitaireNormalAffichage
          : undefined, // Prix unitaire normal (avant remise) pour affichage barré - ligne_prix_src_produit
        priceLineHT: prixLigneHT, // Prix ligne HT
        priceLineHTAvantRemise: hasRemiseProduit
          ? prixLigneHTAvantRemiseProduit
          : undefined, // Prix ligne HT avant remise produit (seulement si remise produit)
        priceLineTTC: prixLigneTTC, // Prix ligne TTC
        hasRemise: hasRemiseProduit, // Indique si cette ligne a une remise produit (pas de remise commande ligne par ligne)
        color: couleur,
        colorFR: ligne.couleur_FR || "",
        colorEN: ligne.couleur_EN || "",
        colorUK: ligne.couleur_UK || "",
        colorHex: ligne.couleur_hex || null,
        colorOfficiel:
          ligne.couleur_officiel != null
            ? Number(ligne.couleur_officiel)
            : null,
        details: quantiteTotal,
        weight: ligne.poids_produit || 0, // Poids unitaire
        weightTotal: poidsTotal, // Poids total de la ligne
        quantite_pack: ligne.quantite_pack || 0,
        quantite_total: quantiteTotal,
        vendu_par: ligne.vendu_par || "",
        imageUrl,
        declinaisons: ligne.declinaisons || null,
        quantites: ligne.quantites,
        // Catégories venant directement du backend
        categorie: (ligne as any).categorie || undefined,
        id_categorie: (ligne as any).id_categorie || undefined,
      };
    });
}

/**
 * Mappe les données de l'API vers OrderProduct[] (produits en rupture, corbeille = 1)
 */
export function mapToProductsRupture(
  data: OrderDetailResponse,
): OrderProduct[] {
  if (!data.lignes || !Array.isArray(data.lignes)) {
    return [];
  }

  // Vérifier si le client est étranger (ne paye pas la TVA)
  const clientEtranger = isEtranger(data);

  // Calculer le taux de TVA selon la date de la commande
  const tvaRate = calculateTVARate(data.dateCommande || data.dateCreation);

  return data.lignes
    .filter((ligne) => ligne.corbeille === 1) // Seulement les produits en rupture
    .map((ligne) => {
      const quantites = ligne.quantites;
      const quantiteTotal =
        ligne.quantite_total ||
        quantites.q1 +
          quantites.q2 +
          quantites.q3 +
          quantites.q4 +
          quantites.q5 +
          quantites.q6 +
          quantites.q7 +
          quantites.q8 +
          quantites.q9 +
          quantites.q10 +
          quantites.q11 +
          quantites.q12;

      // Prix unitaire normal (prix du produit)
      const prixUnitaireNormal = ligne.prix || 0;

      // Prix ligne HT (prixLigne de la ligne - contient déjà la remise produit si applicable)
      // Note: La remise de commande n'est PAS appliquée ligne par ligne, seulement sur le total
      const prixLigneHT = ligne.prixLigne || 0;

      // Prix unitaire calculé comme dans l'ancien système : prixLigne / quantiteTotal
      // (comme dans l'ancien système : $ligne['prixLigne'] / $ligne['quantite_modeles'])
      const prixUnitaire =
        quantiteTotal > 0
          ? prixLigneHT / quantiteTotal
          : ligne.prixReduit || ligne.prix || 0;

      // Prix ligne HT avant remise produit (si prixReduit existe)
      // Calcul: prix unitaire normal * quantité totale
      const prixLigneHTAvantRemiseProduit = prixUnitaireNormal * quantiteTotal;

      // Vérifier si cette ligne a une remise produit (prixReduit différent de prix)
      const hasRemiseProduit =
        ligne.prixReduit != null &&
        ligne.prixReduit > 0 &&
        ligne.prix != null &&
        ligne.prix > 0 &&
        ligne.prixReduit < ligne.prix;

      // Prix unitaire normal (avant remise) pour affichage barré si remise
      // Comme dans l'ancien système : ligne_prix_src_produit
      const prixUnitaireNormalAffichage =
        quantiteTotal > 0 ? prixUnitaireNormal : undefined;

      // Prix ligne TTC (seulement si pas étranger)
      const prixLigneTTC = clientEtranger
        ? prixLigneHT
        : prixLigneHT * (1 + tvaRate);

      // Poids total de la ligne (poids unitaire * quantité)
      const poidsTotal =
        ligne.poids || (ligne.poids_produit || 0) * quantiteTotal;

      // Couleur (utiliser FR par défaut, sinon EN/UK)
      const couleur =
        ligne.couleur_FR || ligne.couleur_EN || ligne.couleur_UK || "";

      // Construire l'URL de l'image (le backend gère déjà la recherche dans le nouveau puis l'ancien système)
      const imageUrl: string | null = ligne.imageUrl || null;

      return {
        id: ligne.id_ligne,
        productId: ligne.id_produit,
        reference: ligne.reference || "",
        price: prixLigneHT, // Prix ligne HT (comme dans l'ancien système)
        priceUnit: prixUnitaire, // Prix unitaire (après remise produit si applicable) - ligne_prix_produit
        priceUnitNormal: hasRemiseProduit
          ? prixUnitaireNormalAffichage
          : undefined, // Prix unitaire normal (avant remise) pour affichage barré - ligne_prix_src_produit
        priceLineHT: prixLigneHT, // Prix ligne HT
        priceLineHTAvantRemise: hasRemiseProduit
          ? prixLigneHTAvantRemiseProduit
          : undefined, // Prix ligne HT avant remise produit (seulement si remise produit)
        priceLineTTC: prixLigneTTC, // Prix ligne TTC
        hasRemise: hasRemiseProduit, // Indique si cette ligne a une remise produit (pas de remise commande ligne par ligne)
        color: couleur,
        colorFR: ligne.couleur_FR || "",
        colorEN: ligne.couleur_EN || "",
        colorUK: ligne.couleur_UK || "",
        colorHex: ligne.couleur_hex || null,
        colorOfficiel:
          ligne.couleur_officiel != null
            ? Number(ligne.couleur_officiel)
            : null,
        details: quantiteTotal,
        weight: ligne.poids_produit || 0, // Poids unitaire
        weightTotal: poidsTotal, // Poids total de la ligne
        quantite_pack: ligne.quantite_pack || 0,
        quantite_total: quantiteTotal,
        vendu_par: ligne.vendu_par || "",
        imageUrl,
        declinaisons: ligne.declinaisons || null,
        quantites: ligne.quantites,
        // Catégories venant directement du backend ! 🎉
        categorie: (ligne as any).categorie || undefined,
        id_categorie: (ligne as any).id_categorie || undefined,
      };
    });
}

/**
 * Mappe les données de l'API vers OrderSummaryData
 */
export function mapToOrderSummary(data: OrderDetailResponse): OrderSummaryData {
  // Calculer la quantité totale depuis les lignes (si disponibles)
  const quantiteTotal =
    data.lignes && Array.isArray(data.lignes)
      ? data.lignes
          .filter((l) => l.corbeille === 0)
          .reduce((sum, ligne) => {
            const q = ligne.quantites;
            return (
              sum +
              q.q1 +
              q.q2 +
              q.q3 +
              q.q4 +
              q.q5 +
              q.q6 +
              q.q7 +
              q.q8 +
              q.q9 +
              q.q10 +
              q.q11 +
              q.q12
            );
          }, 0)
      : 0;

  // Calculer le poids total depuis les lignes (si disponibles)
  const poidsTotal =
    data.lignes && Array.isArray(data.lignes)
      ? data.lignes
          .filter((l) => l.corbeille === 0)
          .reduce((sum, ligne) => sum + ligne.poids, 0)
      : 0;

  // Vérifier si le client est étranger (ne paye pas la TVA)
  const clientEtranger = isEtranger(data);

  // Calculer le taux de TVA selon la date de la commande
  const tvaRate = calculateTVARate(data.dateCommande || data.dateCreation);

  const totalHT = data.recap?.totalHT || data.montantApresRemise || 0;
  // TVA seulement si pas étranger
  const tva = clientEtranger ? 0 : data.recap?.tva || totalHT * tvaRate;
  // TTC = HT si étranger, sinon HT + TVA
  const totalTTC = clientEtranger
    ? totalHT
    : data.recap?.totalTTC || totalHT + tva;

  return {
    totalHT,
    tva,
    totalTTC,
    quantityTotal: data.recap?.quantiteTotal || quantiteTotal,
    weight: data.recap?.poidsTotal || poidsTotal || data.poids || 0,
    packages: data.recap?.nbColis || data.nb_colis || 0,
  };
}

/**
 * Génère le message de remplacement depuis l'option client
 */
export function getReplacementMessage(
  data: OrderDetailResponse,
): string | null {
  if (data.optionClient === true) {
    return "Le client souhaite remplacer les ruptures de stock, VOUS devez impérativement le contacter avant d'expédier sa marchandise. Aucun remplacement ne doit cependant être fait sans la validation du client. Le client ne répond pas ? Sans retour de sa part sous 24h, merci d'expédier la commande.";
  }
  if (data.optionClient === false) {
    return "Le client ne souhaite pas remplacer les éventuelles ruptures de stock, veuillez expédier sans plus attendre";
  }
  return null;
}
